import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { Tracker } from "@/models/tracker";
import { Tag } from "@/models/tag";
import { useMultipleTrackerTags } from "@/hooks/use-tracker-tags";
import TrackerViewSelector, { TrackerViewMode } from "../tracker-view-selector";
import { TrackerTableView, TrackerBoardView, TrackerListView } from "../views";
import ResourceCard from "@/components/resource-card";
import { Collapse } from "@/components/ui";
import GroupHeader from "@/components/group-header";

// Fonction pour obtenir la valeur d'un tracker (réutilisée depuis trackers-list-view)
const getTrackerValue = (tracker: Tracker | undefined) => {
  if (!tracker) return;

  if (tracker.is_import)
    return {
      source: "import",
      key: "name",
      value: tracker.metadata.name,
    };
  const key =
    Object.keys(tracker.params).length > 0
      ? Object.keys(tracker.params).filter((e) => e !== "metadata")[0]
      : undefined;
  return key
    ? {
        key,
        value: `${tracker.params[key]}`,
        source: tracker.source,
      }
    : undefined;
};

interface TrackersViewWrapperProps {
  trackers: Tracker[];
  onToggleActive?: (trackerId: string, isEnabled: boolean) => void;
  onOpenExecutions?: (tracker: Tracker) => void;
  searchTerm?: string;
  selectedTags?: Tag[];
  groupBy?: "none" | "source" | "tags" | "status" | "type";
  viewMode?: TrackerViewMode;
  onViewChange?: (view: TrackerViewMode) => void;
  showViewSelector?: boolean;
  showGroupSelector?: boolean;
  onTagsUpdate?: (trackerId: string, tags: Tag[]) => void;
  className?: string;
  /** Selected tracker IDs for selection mode */
  selectedIds?: string[];
  /** Callback when a tracker is selected/deselected */
  onSelect?: (trackerId: string, selected: boolean) => void;
}

const TrackersViewWrapper: React.FC<TrackersViewWrapperProps> = ({
  trackers,
  onToggleActive,
  onOpenExecutions,
  searchTerm = "",
  selectedTags = [],
  groupBy = "source",
  viewMode = "board",
  onViewChange,
  showViewSelector = true,
  showGroupSelector = true,
  onTagsUpdate,
  className = "",
  selectedIds = [],
  onSelect,
}) => {
  const { trackersTagsMap: rawTrackersTagsMap, updateTrackerTags } = useMultipleTrackerTags();
  
  // Stabiliser trackersTagsMap pour éviter les re-renders infinis
  const prevTrackersTagsMapRef = useRef<Record<string, string[]>>({});
  const trackersTagsMap = useMemo(() => {
    // Comparer les clés pour détecter les changements réels
    const prevKeys = Object.keys(prevTrackersTagsMapRef.current)
      .sort()
      .join(",");
    const currentKeys = Object.keys(rawTrackersTagsMap).sort().join(",");

    // Si les clés sont identiques, vérifier si les valeurs ont changé
    if (prevKeys === currentKeys) {
      let hasChanged = false;
      for (const key in rawTrackersTagsMap) {
        const prevTags = prevTrackersTagsMapRef.current[key] || [];
        const currentTags = rawTrackersTagsMap[key] || [];
        if (
          prevTags.length !== currentTags.length ||
          !prevTags.every((tag, i) => tag === currentTags[i])
        ) {
          hasChanged = true;
          break;
        }
      }
      if (!hasChanged) {
        // Retourner la version précédente pour éviter les re-renders
        return prevTrackersTagsMapRef.current;
      }
    }

    // Mettre à jour la référence
    prevTrackersTagsMapRef.current = rawTrackersTagsMap;
    return rawTrackersTagsMap;
  }, [rawTrackersTagsMap]);
  
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set()
  );
  const [internalViewMode, setInternalViewMode] =
    useState<TrackerViewMode>(viewMode);

  useEffect(() => {
    setInternalViewMode(viewMode);
  }, [viewMode]);

  // Fonction pour obtenir la source d'un tracker
  const getTrackerSource = (tracker: Tracker): string => {
    if (tracker.is_import) return "Import";
    if (!tracker.path) return tracker.source || "Autre";
    const pathParts = tracker.path.split("/").filter(Boolean);
    return pathParts[0] || tracker.source || "Autre";
  };

  // Filtrage des trackers
  const filteredTrackers = useMemo(() => {
    if (!trackers) return [];

    return trackers.filter((tracker) => {
      const trackerValue = getTrackerValue(tracker);
      const matchesSearch =
        !searchTerm ||
        tracker.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (trackerValue?.value &&
          trackerValue.value.toLowerCase().includes(searchTerm.toLowerCase()));

      // Filtrage par tags
      const matchesTags =
        selectedTags.length === 0 ||
        (trackersTagsMap[tracker.id] &&
          selectedTags.some((selectedTag) =>
            trackersTagsMap[tracker.id].some(
              (trackerTagId) => {
                const tagId = typeof selectedTag === 'string' ? selectedTag : (selectedTag as Tag).id;
                return trackerTagId === tagId;
              }
            )
          ));

      return matchesSearch && matchesTags;
    });
  }, [trackers, searchTerm, selectedTags, trackersTagsMap]);

  // Groupement des trackers
  const groupedTrackers = useMemo(() => {
    if (groupBy === "none") {
      return { "": filteredTrackers };
    }

    const groups: Record<string, Tracker[]> = {};

    filteredTrackers.forEach((tracker) => {
      switch (groupBy) {
        case "source": {
          const groupKey = getTrackerSource(tracker);
          if (!groups[groupKey]) {
            groups[groupKey] = [];
          }
          groups[groupKey].push(tracker);
          break;
        }
        case "tags": {
          const trackerTags = trackersTagsMap[tracker.id] || [];
          if (trackerTags.length === 0) {
            // Tracker sans tags -> groupe "Sans tags"
            if (!groups["Sans tags"]) {
              groups["Sans tags"] = [];
            }
            groups["Sans tags"].push(tracker);
          } else {
            // Ajouter le tracker à chaque groupe de tag individuel
            trackerTags.forEach((tag) => {
              if (!groups[tag]) {
                groups[tag] = [];
              }
              groups[tag].push(tracker);
            });
          }
          break;
        }
        case "status": {
          const groupKey = tracker.is_import
            ? "Import"
            : tracker.status === "active"
            ? "Actifs"
            : "Inactifs";
          if (!groups[groupKey]) {
            groups[groupKey] = [];
          }
          groups[groupKey].push(tracker);
          break;
      }
        case "type": {
          const groupKey = tracker.is_import ? "Import" : "Monitoring";
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(tracker);
          break;
        }
      }
    });

    // Trier les clés de groupe
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      if (a === "Sans tags" || a === "Autre") return 1;
      if (b === "Sans tags" || b === "Autre") return -1;
      return a.localeCompare(b);
    });

    const sortedGroups: Record<string, Tracker[]> = {};
    sortedKeys.forEach((key) => {
      sortedGroups[key] = groups[key];
    });

    return sortedGroups;
  }, [filteredTrackers, groupBy, trackersTagsMap]);

  const toggleGroup = (groupKey: string) => {
    setCollapsedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  };

  const handleViewChangeInternal = (view: TrackerViewMode) => {
    setInternalViewMode(view);
    if (onViewChange) {
      onViewChange(view);
    }
  };

  const handleToggleActive = (trackerId: string, isEnabled: boolean) => {
    if (onToggleActive) {
      onToggleActive(trackerId, isEnabled);
    }
  };

  const handleOpenExecutions = (tracker: Tracker) => {
    if (onOpenExecutions) {
      onOpenExecutions(tracker);
    }
  };

  // Wrapper function to handle the type conversion
  const handleTagsUpdate = useCallback((trackerId: string, tags: string[]) => {
    if (onTagsUpdate) {
      // If parent expects Tag[], convert string[] to Tag[]
      // This assumes Tag has at least an id property
      const tagObjects = tags.map(tagId => ({ id: tagId } as Tag));
      onTagsUpdate(trackerId, tagObjects);
    } else {
      // Use the local updateTrackerTags which expects string[]
      updateTrackerTags(trackerId, tags);
    }
  }, [onTagsUpdate, updateTrackerTags]);

  const commonProps = {
    onToggleActive: handleToggleActive,
    onOpenExecutions: handleOpenExecutions,
    getTrackerValue,
    trackersTagsMap,
    onTagsUpdate: handleTagsUpdate,
    groupBy,
    groupedTrackers: groupBy !== "none" ? groupedTrackers : undefined,
    collapsedGroups,
    onToggleGroup: toggleGroup,
  };

  const renderView = () => {
    const trackersToDisplay =
      groupBy === "none"
        ? filteredTrackers
        : Object.values(groupedTrackers).flat();

    switch (internalViewMode) {
      case "table":
        return (
          <TrackerTableView trackers={trackersToDisplay} {...commonProps} />
        );
      case "board":
        return (
          <TrackerBoardView trackers={filteredTrackers} {...commonProps} />
        );
      case "list":
        return (
          <TrackerListView trackers={trackersToDisplay} {...commonProps} />
        );
      case "gallery":
      default:
        if (groupBy === "none") {
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredTrackers.map((tracker) => (
                <ResourceCard
                  key={tracker.id}
                  tracker={tracker}
                  resourceType="tracker"
                  onToggleActive={handleToggleActive}
                  onTagsUpdate={handleTagsUpdate}
                  tagsMap={trackersTagsMap}
                />
              ))}
            </div>
          );
        } else {
          return (
            <div className="flex flex-col gap-6">
              {Object.entries(groupedTrackers).map(
                ([groupKey, groupTrackers]) => {
                  const isCollapsed = collapsedGroups.has(groupKey);
                  const groupCount = groupTrackers.length;

                  return (
                    <div key={groupKey} className="flex flex-col gap-3">
                      <GroupHeader
                        groupKey={groupKey}
                        groupCount={groupCount}
                        groupBy={groupBy}
                        isCollapsed={isCollapsed}
                        onToggle={() => toggleGroup(groupKey)}
                        resourceType="tracker"
                        emptyGroupLabel="Tous les trackers"
                      />
                      <Collapse in={!isCollapsed}>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 pl-6">
                          {groupTrackers.map((tracker) => (
                            <ResourceCard
                              key={tracker.id}
                              tracker={tracker}
                              resourceType="tracker"
                              onToggleActive={handleToggleActive}
                              onTagsUpdate={handleTagsUpdate}
                              tagsMap={trackersTagsMap}
                            />
                          ))}
                        </div>
                      </Collapse>
                    </div>
                  );
                }
              )}
            </div>
          );
        }
    }
  };

  return (
    <div className={className}>
      {(showViewSelector || showGroupSelector) && (
        <div className="flex items-center gap-4 mb-4">
          {showViewSelector && onViewChange && (
            <TrackerViewSelector
              currentView={internalViewMode}
              onViewChange={handleViewChangeInternal}
            />
          )}
        </div>
      )}
      {renderView()}
    </div>
  );
};

export default TrackersViewWrapper;
